import React, { useEffect } from 'react'
import { Button, Grid } from '@mui/material'
import { array, object, string } from 'yup'
import { useFormContext } from 'react-hook-form'

import { Dialog } from '../dialog.tsx'
import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  SubmitButton,
  TextFieldControl,
} from '../form'
import { formatMoney, preventHandler } from '../../utils.ts'
import { RESEND_ENDPOINTS, useMutateSbpRefund } from '../../services'
import { useSnackbarContext } from '../snackbar'
import { OfdPosition, RefundOperationParams, SbpPayment } from '../../types.ts'
import { useStages } from '../../hooks'
import { FadeInWrapper } from '../fade-in-wrapper.tsx'
import { FormControl } from '../form/controls/form-control.tsx'
import { OfdFields } from '../ofd-fields'

import { ConfirmRefundStage } from './confirm-refund-stage.tsx'
import { RefundFormState } from './refund-button.types.ts'
import { useUser } from '@/stores/slices/user-slice.ts'

const refundFormSchema = (availableForReverse: number | null, isVerificationStage = false, withOfd = false) =>
  object<FormSchema<RefundFormState>>({
    reverseSum: string()
      .required(FormSchemaMessages.required)
      .test(FormSchemaTests.isLessOrEqual(availableForReverse))
      .test(FormSchemaTests.isNotZero),
    emailCode: isVerificationStage ? string().required(FormSchemaMessages.required) : string(),
    email: withOfd ? string().required(FormSchemaMessages.required) : string(),
    // валидация каждой позиции происходит в ofd-position-dialog.tsx
    positions: withOfd ? array().required('Необходимо добавить позиции') : array(),
  })

export interface RefundDialogProps extends Pick<SbpPayment, 'availableForReverse' | 'operationId'> {
  onClose(): void
  isStaticQr: boolean
}

export const RefundDialog = ({
  availableForReverse,
  operationId,
  isStaticQr,
  onClose,
}: RefundDialogProps): React.ReactElement => {
  const { stage, handleNextState } = useStages()
  const { addSnack } = useSnackbarContext()
  const user = useUser()
  const handleRefund = useMutateSbpRefund(operationId.toString())

  const handleRefundInit = async (formValues: RefundFormState) => {
    const bodyParams: RefundOperationParams = formValues
    if (formValues.positions?.length) {
      bodyParams.positions = formValues.positions?.map(({ id, ...position }) => position as OfdPosition)
    }

    await handleRefund.mutateAsync(bodyParams)
  }

  useEffect(() => {
    switch (handleRefund.status) {
      case 'success': {
        const response = handleRefund.data?.data
        const isSuccess = response?.state.toUpperCase() === 'OK'

        const errorMessage = response?.errorMessage ?? 'Произошла ошибка при создании возврата, попробуйте ещё раз'

        addSnack({
          message: isSuccess
            ? 'Операция на возврат создана и принята в обработку. Обновите список заявок через некоторое время, чтобы увидеть результат'
            : errorMessage,
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })
        if (isSuccess) {
          onClose()
          handleRefund.reset()
        }
        break
      }
      case 'error': {
        // бэк не выбрасывает ошибку если ввести возврат больше доступной суммы
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        handleRefund.reset()
        break
      }
      default:
        break
    }
  }, [handleRefund.status])

  const isVerificationStage = stage === 1
  const displayOfdFields = user?.permissions?.ofd && !isStaticQr

  return (
    <Form<RefundFormState>
      name="refundOperation"
      onSubmit={preventHandler(handleRefundInit, handleRefund.isPending)}
      validationSchema={refundFormSchema(availableForReverse, isVerificationStage, displayOfdFields)}
    >
      <Dialog
        open
        maxWidth="lg"
        content={
          <>
            {stage === 0 && (
              <FadeInWrapper>
                <Grid container flexDirection="column" spacing={2}>
                  <Grid item>
                    <TextFieldControl<RefundFormState>
                      name="reverseSum"
                      fullWidth
                      helperText={
                        availableForReverse !== null ? `Доступно к возврату ${formatMoney(availableForReverse)}` : ''
                      }
                      label="Cумма возврата"
                      size="small"
                      normalize={InputNormalizers.positiveNumbersOnly()}
                    />
                  </Grid>
                  <Grid item>
                    <TextFieldControl<RefundFormState>
                      name="purpose"
                      fullWidth
                      label="Назначение возврата"
                      size="small"
                    />
                  </Grid>
                  {displayOfdFields && (
                    <Grid item>
                      <FormControl name="positions" component={OfdFields} />
                    </Grid>
                  )}
                </Grid>
              </FadeInWrapper>
            )}
            {isVerificationStage && <ConfirmRefundStage resendCodeEndpoint={RESEND_ENDPOINTS.resendSbpCode} />}
          </>
        }
        actions={
          <>
            {stage === 0 && (
              <>
                <Button fullWidth onClick={onClose} disabled={handleRefund.isPending} size="large" color="secondary">
                  Отмена
                </Button>
                <RefundVerificationCodeButton handleNextStage={handleNextState} />
              </>
            )}
            {isVerificationStage && <CreateRefundButton />}
          </>
        }
        onClose={onClose}
        title={`Возврат по заказу №${operationId}`}
      />
    </Form>
  )
}

const RefundVerificationCodeButton = ({ handleNextStage }: { handleNextStage(): void }): React.ReactElement => {
  const { formState } = useFormContext()
  const { isValid } = formState

  return (
    <Button fullWidth size="large" disabled={!isValid} onClick={handleNextStage}>
      Продолжить
    </Button>
  )
}

const CreateRefundButton = (): React.ReactElement => {
  const { formState } = useFormContext()
  const { isValid } = formState

  return (
    <SubmitButton form="refundOperation" disabled={!isValid}>
      Сделать возврат
    </SubmitButton>
  )
}
